import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Stack, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ExpandMore, Code } from '@mui/icons-material';
import { Highlight, themes } from 'prism-react-renderer';
import { useThemeMode } from '../../contexts/ThemeContext';

const ComponentDemo = ({ 
  title, 
  description, 
  component, 
  usage, 
  props = [] 
}) => {
  const [showCode, setShowCode] = useState(false);
  const { isDark } = useThemeMode();

  return (
    <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight={600}>
              {title}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Toggle Code View">
                <IconButton 
                  onClick={() => setShowCode(!showCode)}
                  color={showCode ? "primary" : "default"}
                >
                  <Code />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {description}
          </Typography>
        </Box>

        {/* Component Preview */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 3,
          backgroundColor: 'background.default'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Preview
          </Typography>
          {component}
        </Box>

        {/* Code Example */}
        {showCode && (
          <Accordion defaultExpanded sx={{ backgroundColor: 'background.paper' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Usage Example</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Highlight
                theme={isDark ? themes.vsDark : themes.github}
                code={usage}
                language="jsx"
              >
                {({ className, style, tokens, getLineProps, getTokenProps }) => (
                  <pre className={className} style={{ 
                    ...style, 
                    padding: '1rem',
                    borderRadius: '8px',
                    overflow: 'auto'
                  }}>
                    {tokens.map((line, i) => (
                      <div key={i} {...getLineProps({ line, key: i })}>
                        {line.map((token, key) => (
                          <span key={key} {...getTokenProps({ token, key })} />
                        ))}
                      </div>
                    ))}
                  </pre>
                )}
              </Highlight>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Props Table */}
        {props.length > 0 && (
          <Accordion sx={{ backgroundColor: 'background.paper' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Props</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Prop</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props.map((prop, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <code style={{ 
                            backgroundColor: 'rgba(127, 127, 127, 0.1)', 
                            padding: '2px 6px', 
                            borderRadius: '4px',
                            fontFamily: 'monospace'
                          }}>
                            {prop.name}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={prop.type} 
                            size="small" 
                            variant="outlined"
                            color={prop.type === 'string' ? 'primary' : 
                                   prop.type === 'number' ? 'success' : 
                                   prop.type === 'boolean' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{prop.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}
      </Stack>
    </Paper>
  );
};

export default ComponentDemo;